import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { listInboundByStaffRecipient } from "../../services/emailCorrespondenceApi";
import { resolveEmailPlatformOrganizationGuid } from "../../utils/emailPlatformOrganization";
import useUserData from "../../hooks/useUserData";
import { getAvatarColor, getInitials } from "../../utils/avatarUtils";
import {
  apiOk,
  buildInboxRows,
  rowFrom,
  rowId,
  rowPreview,
  rowReceivedDisplay,
  rowSubject,
  unreadInboxRows,
} from "../../utils/notificationInboxUtils";
import "./HeaderInboxDropdown.scss";

const INBOX_FETCH_TAKE = 200;
const DROPDOWN_UNREAD_LIMIT = 5;
const REFRESH_MS = 60_000;

function senderLabel(from) {
  const s = String(from || "").trim();
  if (!s) return "Unknown";
  const named = s.match(/^(.+?)\s*<([^>]+)>$/);
  if (named) {
    const name = named[1].trim();
    if (name) return name;
    return named[2].trim();
  }
  return s;
}

function senderEmail(from) {
  const s = String(from || "").trim();
  const angle = s.match(/<([^>]+)>/);
  return angle ? angle[1].trim() : s;
}

const HeaderInboxDropdown = () => {
  const navigate = useNavigate();
  const { userData } = useUserData();
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const organizationRefId = useMemo(
    () => resolveEmailPlatformOrganizationGuid(userData),
    [userData],
  );

  const loadInbox = useCallback(async () => {
    if (!organizationRefId) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const res = await listInboundByStaffRecipient({
        organizationRefId,
        take: INBOX_FETCH_TAKE,
      });
      if (!apiOk(res)) {
        setRows([]);
        return;
      }
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [organizationRefId]);

  useEffect(() => {
    loadInbox();
  }, [loadInbox]);

  useEffect(() => {
    if (!organizationRefId) return undefined;
    const timer = window.setInterval(loadInbox, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [organizationRefId, loadInbox]);

  useEffect(() => {
    if (open) loadInbox();
  }, [open, loadInbox]);

  const staffEmail = useMemo(
    () => String(userData?.EmailId || userData?.emailId || "").trim(),
    [userData?.EmailId, userData?.emailId],
  );

  const unreadRows = useMemo(
    () => unreadInboxRows(buildInboxRows(rows, staffEmail)),
    [rows, staffEmail],
  );
  const unreadCount = unreadRows.length;
  const dropdownRows = useMemo(
    () => unreadRows.slice(0, DROPDOWN_UNREAD_LIMIT),
    [unreadRows],
  );

  const badgeLabel = unreadCount > 99 ? "99+" : String(unreadCount);

  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const dropdownWidth = 360;
    const dropdownHeight = 320;
    const spaceBelow = window.innerHeight - rect.bottom;

    setPosition({
      top: spaceBelow < dropdownHeight ? rect.top - dropdownHeight : rect.bottom + 6,
      left: Math.max(8, Math.min(rect.right - dropdownWidth, window.innerWidth - dropdownWidth - 8)),
    });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (e) => {
      if (
        !dropdownRef.current?.contains(e.target) &&
        !triggerRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const goToInbox = () => {
    setOpen(false);
    navigate("/notification-center");
  };

  const handleItemClick = (row) => {
    setOpen(false);
    const id = rowId(row);
    navigate("/notification-center", id ? { state: { selectedMessageId: id } } : undefined);
  };

  return (
    <div className="header-inbox-dropdown">
      <button
        ref={triggerRef}
        type="button"
        className="header-action-btn header-action-btn-icon-only position-relative"
        onClick={() => setOpen((v) => !v)}
        aria-label={
          unreadCount
            ? `Notifications, ${unreadCount} unread`
            : "Notifications"
        }
        aria-expanded={open}
        aria-haspopup="true"
      >
        <i className="far fa-bell header-action-icon" aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="header-action-badge" aria-hidden="true">
            {badgeLabel}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={dropdownRef}
          className="header-inbox-dropdown-panel shadow-sm"
          style={{ top: position.top, left: position.left }}
          role="menu"
          aria-label="Unread inbox notifications"
        >
          <div className="header-inbox-dropdown-head">
            <span className="header-inbox-dropdown-title">Inbox</span>
            {unreadCount > 0 && (
              <span className="header-inbox-dropdown-count">
                {unreadCount} unread
              </span>
            )}
          </div>

          <div className="header-inbox-dropdown-body">
            {loading && rows.length === 0 ? (
              <div className="header-inbox-dropdown-empty">
                <i className="fas fa-spinner fa-spin me-2" aria-hidden="true" />
                Loading…
              </div>
            ) : dropdownRows.length === 0 ? (
              <div className="header-inbox-dropdown-empty">
                No unread messages
              </div>
            ) : (
              <ul className="header-inbox-dropdown-list">
                {dropdownRows.map((row) => {
                  const id = rowId(row);
                  const preview = rowPreview(row);
                  const from = rowFrom(row);
                  const label = senderLabel(from);
                  const email = senderEmail(from);
                  const avatarTitle = label !== email ? `${label} (${email})` : email;
                  return (
                    <li key={id || rowSubject(row)}>
                      <button
                        type="button"
                        className="header-inbox-dropdown-item"
                        onClick={() => handleItemClick(row)}
                        role="menuitem"
                      >
                        <span
                          className="header-inbox-dropdown-avatar"
                          style={{ backgroundColor: getAvatarColor(label || email) }}
                          title={avatarTitle}
                          aria-label={`From: ${avatarTitle}`}
                        >
                          {getInitials(label || email)}
                        </span>
                        <span className="header-inbox-dropdown-content">
                          <span className="header-inbox-dropdown-subject">
                            {rowSubject(row)}
                          </span>
                          {preview ? (
                            <span className="header-inbox-dropdown-preview">
                              {preview}
                            </span>
                          ) : null}
                          <span className="header-inbox-dropdown-time">
                            {rowReceivedDisplay(row)}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="header-inbox-dropdown-foot">
            <button
              type="button"
              className="header-inbox-dropdown-view-all"
              onClick={goToInbox}
            >
              View all in inbox
              {unreadCount > DROPDOWN_UNREAD_LIMIT
                ? ` (${unreadCount - DROPDOWN_UNREAD_LIMIT} more unread)`
                : ""}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeaderInboxDropdown;
